<template>
    <v-dialog :value="open" max-width="900" persistent scrollable>
        <v-card>
            <v-card-title>
                <v-icon left>mdi-account-cog</v-icon>
                Permisos de <strong class="ml-1">{{ userName }}</strong>
                <v-chip small class="ml-2" outlined>{{ companyName }}</v-chip>
                <v-spacer />
                <v-btn icon @click="$emit('close')">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>

            <v-divider />

            <v-card-text style="max-height: 70vh;">
                <loading v-if="loading" />
                <div v-else>
                    <div class="caption grey--text mb-2">
                        Rol base: <strong>{{ roleName || 'Sin rol' }}</strong>
                    </div>

                    <v-row>
                        <!-- Additional permissions -->
                        <v-col cols="12" md="6">
                            <div class="subtitle-2 mb-2 success--text">
                                <v-icon small color="success" left>mdi-plus-circle</v-icon>
                                Permisos adicionales
                                <span class="caption grey--text ml-1">(permisos extra sobre el rol)</span>
                            </div>
                            <div class="d-flex mb-2">
                                <v-btn x-small text color="success" @click="selectAllAdditional">Agregar todos</v-btn>
                                <v-btn x-small text color="grey" @click="form.additionalPermissions = []">Limpiar</v-btn>
                            </div>
                            <v-virtual-scroll
                                :items="permissionsNotInRole"
                                height="350"
                                item-height="36"
                            >
                                <template v-slot:default="{ item }">
                                    <v-checkbox
                                        v-model="form.additionalPermissions"
                                        :value="item"
                                        :label="item"
                                        dense
                                        hide-details
                                        class="mt-0 pt-0"
                                    />
                                </template>
                            </v-virtual-scroll>
                        </v-col>

                        <!-- Revoked permissions -->
                        <v-col cols="12" md="6">
                            <div class="subtitle-2 mb-2 error--text">
                                <v-icon small color="error" left>mdi-minus-circle</v-icon>
                                Permisos revocados
                                <span class="caption grey--text ml-1">(permisos del rol que se quitan)</span>
                            </div>
                            <div class="d-flex mb-2">
                                <v-btn x-small text color="error" @click="revokeAll">Revocar todos</v-btn>
                                <v-btn x-small text color="grey" @click="form.revokedPermissions = []">Limpiar</v-btn>
                            </div>
                            <v-virtual-scroll
                                :items="rolePermissions"
                                height="350"
                                item-height="36"
                            >
                                <template v-slot:default="{ item }">
                                    <v-checkbox
                                        v-model="form.revokedPermissions"
                                        :value="item"
                                        :label="item"
                                        dense
                                        hide-details
                                        class="mt-0 pt-0"
                                    />
                                </template>
                            </v-virtual-scroll>
                        </v-col>
                    </v-row>

                    <v-alert v-if="errorMessage" type="error" class="mt-3" dense>
                        {{ errorMessage }}
                    </v-alert>
                </div>
            </v-card-text>

            <v-divider />

            <v-card-actions>
                <v-btn text color="error" @click="clearOverrides" :disabled="saving">
                    Quitar overrides
                </v-btn>
                <v-spacer />
                <v-btn text @click="$emit('close')">Cancelar</v-btn>
                <v-btn color="primary" @click="save" :loading="saving">Guardar</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import UserPermissionProvider from '../../../providers/UserPermissionProvider'
import InviraRoleProvider from '../../../providers/InviraRoleProvider'
import { Loading } from '@dracul/common-frontend'

export default {
    name: 'UserPermissionsEdit',
    components: { Loading },
    props: {
        open: { type: Boolean, default: true },
        userId: { type: String, required: true },
        companyId: { type: String, required: true },
        userName: { type: String, default: '' },
        companyName: { type: String, default: '' },
        roleId: { type: String, default: null },
        roleName: { type: String, default: null }
    },
    data() {
        return {
            loading: true,
            saving: false,
            errorMessage: '',
            allPermissions: [],
            rolePermissions: [],
            form: {
                additionalPermissions: [],
                revokedPermissions: []
            }
        }
    },
    computed: {
        permissionsNotInRole() {
            const roleSet = new Set(this.rolePermissions)
            return this.allPermissions.filter(p => !roleSet.has(p))
        }
    },
    created() {
        this.loadData()
    },
    methods: {
        async loadData() {
            this.loading = true
            try {
                // Load all available permissions
                const rolesR = await InviraRoleProvider.fetchRoles()
                const allRoles = rolesR.data.adminInviraRoleFetch || []
                const permSet = new Set()
                allRoles.forEach(r => (r.permissions || []).forEach(p => permSet.add(p)))
                this.allPermissions = Array.from(permSet).sort()

                // Load role permissions if roleId provided
                if (this.roleId) {
                    const roleR = await InviraRoleProvider.findRole(this.roleId)
                    const role = roleR.data.adminInviraRoleFind
                    this.rolePermissions = role ? (role.permissions || []).slice().sort() : []
                }

                // Load existing overrides
                const permR = await UserPermissionProvider.findUserPermission(this.userId, this.companyId)
                const existing = permR.data.adminUserPermissionFind
                if (existing) {
                    this.form.additionalPermissions = existing.additionalPermissions || []
                    this.form.revokedPermissions = existing.revokedPermissions || []
                }
            } catch (e) {
                console.error('[UserPermissionsEdit] Error loading data:', e)
            } finally {
                this.loading = false
            }
        },
        selectAllAdditional() {
            this.form.additionalPermissions = [...this.permissionsNotInRole]
        },
        revokeAll() {
            this.form.revokedPermissions = [...this.rolePermissions]
        },
        save() {
            this.saving = true
            this.errorMessage = ''
            UserPermissionProvider.upsertUserPermission(this.userId, this.companyId, {
                additionalPermissions: this.form.additionalPermissions,
                revokedPermissions: this.form.revokedPermissions
            })
                .then(() => {
                    this.$emit('saved')
                })
                .catch(e => {
                    this.errorMessage = e.message || 'Error al guardar permisos'
                })
                .finally(() => this.saving = false)
        },
        clearOverrides() {
            this.saving = true
            UserPermissionProvider.deleteUserPermission(this.userId, this.companyId)
                .then(() => {
                    this.$emit('saved')
                })
                .catch(e => {
                    this.errorMessage = e.message || 'Error al eliminar overrides'
                })
                .finally(() => this.saving = false)
        }
    }
}
</script>
